/**
 * HubTenantDetailPage — HUB-REDESIGN P3.
 *
 * Reshelled from the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/tenants.jsx onto the
 * shared Card/KPI/Pill/Endpoint atoms. Preserves the full operator
 * surface from the prior page: lifecycle actions (extend trial /
 * suspend / reactivate / hard-delete / restore), feature-flags toggles
 * with bulk scope, Sage cost rollup, outbound delivery log, audit log,
 * users, billing history. Header / lifecycle / activity / usage /
 * feature flags / audit grid follows the dense 12-col bento from the
 * design.
 *
 * Impersonate is intentionally omitted: legacy /god-view still owns
 * the cross-origin handoff to app.usetenon.com (HUB-12-13b).
 */
import React, { useEffect, useMemo, useState } from 'react'
import { formatCurrency } from '@/utils/formatCurrency'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card, Endpoint, KPI, Pill, StatusDot, Icon, AreaChart,
} from './components'
import {
  hubTenants, hubFeatureFlags,
  type HubTenantDetail,
  type HubTenantAuditEntry,
  type HubTenantFeatureFlags,
  type HubTenantSageUsage,
  type HubTenantOutboundLog,
} from './api'
import { posthogCapture } from './analytics'
import { formatDateWithOptions, formatDateTime } from '@/utils/dateUtils'

type ActionKind =
  | null
  | 'suspend'
  | 'reactivate'
  | 'extend_trial'
  | 'hard_delete'
  | 'restore'
  | 'refund'
  | 'credit'
  | 'one_off_invoice'
  | 'send_billing_email'

function statusPillVariant(status: string): 'live' | 'info' | 'err' | 'warn' | 'ghost' {
  if (status === 'active') return 'live'
  if (status === 'trialing') return 'info'
  if (status === 'past_due' || status === 'canceled' || status === 'cancelled') return 'err'
  if (status === 'suspended') return 'warn'
  return 'ghost'
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'never'
  const diff = Date.now() - new Date(dateStr).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

function formatCents(cents: number): string {
  if (cents === 0) return '$0.00'
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function HubTenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<HubTenantDetail | null>(null)
  const [audit, setAudit] = useState<HubTenantAuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pendingAction, setPendingAction] = useState<ActionKind>(null)
  const [extendDays, setExtendDays] = useState('7')
  const [deleteConfirm, setDeleteConfirm] = useState('')
  // HUB-12-7 — billing-action modal state
  const [refundInvoiceID, setRefundInvoiceID] = useState('')
  const [refundAmount, setRefundAmount] = useState('')   // dollars; '' = full refund
  const [refundReason, setRefundReason] = useState('')
  const [creditAmount, setCreditAmount] = useState('')   // dollars
  const [creditCurrency, setCreditCurrency] = useState('cad')
  const [creditReason, setCreditReason] = useState('')
  const [oneOffAmount, setOneOffAmount] = useState('')   // dollars
  const [oneOffCurrency, setOneOffCurrency] = useState('cad')
  const [oneOffDescription, setOneOffDescription] = useState('')
  const [oneOffReason, setOneOffReason] = useState('')
  const [oneOffSendNow, setOneOffSendNow] = useState(true)

  // HUB-7 — billing email modal state
  const [billingEmailTemplate, setBillingEmailTemplate] = useState<'trial-ending' | 'past-due' | 'dunning' | 'custom'>('trial-ending')
  const [billingEmailSubject, setBillingEmailSubject] = useState('')
  const [billingEmailBody, setBillingEmailBody] = useState('')

  const resetBillingForms = () => {
    setRefundInvoiceID(''); setRefundAmount(''); setRefundReason('')
    setCreditAmount(''); setCreditCurrency('cad'); setCreditReason('')
    setOneOffAmount(''); setOneOffCurrency('cad'); setOneOffDescription('')
    setOneOffReason(''); setOneOffSendNow(true)
    setBillingEmailTemplate('trial-ending'); setBillingEmailSubject(''); setBillingEmailBody('')
  }
  // dollars-string → cents-int (rejects negatives + NaN)
  const dollarsToCents = (s: string): number | null => {
    const n = parseFloat(s)
    if (!Number.isFinite(n) || n <= 0) return null
    return Math.round(n * 100)
  }

  const refresh = async () => {
    if (!id) return
    try {
      const [d, a] = await Promise.all([
        hubTenants.get(id),
        hubTenants.auditLog(id, 50).catch(() => ({ items: [] as HubTenantAuditEntry[], total: 0 })),
      ])
      setData(d)
      setAudit(a.items || [])
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load tenant')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() /* eslint-disable-next-line */ }, [id])

  const runAction = async () => {
    if (!pendingAction || !id) return
    setBusy(true)
    try {
      if (pendingAction === 'suspend') await hubTenants.suspend(id)
      else if (pendingAction === 'reactivate') await hubTenants.reactivate(id)
      else if (pendingAction === 'extend_trial') {
        const n = Math.max(1, parseInt(extendDays || '7'))
        await hubTenants.extendTrial(id, n)
      } else if (pendingAction === 'hard_delete') {
        await hubTenants.hardDelete(id, deleteConfirm)
      } else if (pendingAction === 'restore') {
        await hubTenants.restore(id)
      } else if (pendingAction === 'refund') {
        const amt = refundAmount.trim() === '' ? undefined : (dollarsToCents(refundAmount) ?? undefined)
        if (refundAmount.trim() !== '' && amt === undefined) throw new Error('Invalid refund amount')
        if (!refundInvoiceID.trim()) throw new Error('Invoice ID required')
        if (!refundReason.trim()) throw new Error('Reason required for audit log')
        await hubTenants.refund(id, {
          invoice_id: refundInvoiceID.trim(),
          amount_cents: amt,
          reason: refundReason.trim(),
        })
      } else if (pendingAction === 'credit') {
        const amt = dollarsToCents(creditAmount)
        if (!amt) throw new Error('Credit amount must be a positive number')
        if (!creditReason.trim()) throw new Error('Reason required for audit log')
        await hubTenants.credit(id, {
          amount_cents: amt,
          currency: creditCurrency,
          reason: creditReason.trim(),
        })
      } else if (pendingAction === 'send_billing_email') {
        const emailBody: Parameters<typeof hubTenants.sendBillingEmail>[1] = { template: billingEmailTemplate }
        if (billingEmailTemplate === 'custom') {
          if (!billingEmailSubject.trim()) throw new Error('Subject required for custom email')
          if (!billingEmailBody.trim()) throw new Error('Body required for custom email')
          emailBody.subject = billingEmailSubject.trim()
          emailBody.html_body = billingEmailBody.trim()
        }
        const res = await hubTenants.sendBillingEmail(id, emailBody)
        if (res.failed > 0 && res.sent === 0) throw new Error(`Send failed for all ${res.failed} recipient(s)`)
      } else if (pendingAction === 'one_off_invoice') {
        const amt = dollarsToCents(oneOffAmount)
        if (!amt) throw new Error('Invoice amount must be a positive number')
        if (!oneOffDescription.trim()) throw new Error('Description required (shown to customer)')
        if (!oneOffReason.trim()) throw new Error('Reason required for audit log')
        await hubTenants.oneOffInvoice(id, {
          amount_cents: amt,
          currency: oneOffCurrency,
          description: oneOffDescription.trim(),
          reason: oneOffReason.trim(),
          send_now: oneOffSendNow,
        })
      }
      posthogCapture(`hub_tenant_${pendingAction}`, { tenant_id: id })
      setPendingAction(null)
      setDeleteConfirm('')
      resetBillingForms()
      await refresh()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
  }
  if (err && !data) {
    return <div style={{ padding: 14, color: 'var(--err)', fontSize: 13 }}>{err}</div>
  }
  if (!data) return null

  const { company, users, subscription, billing_history, usage, flags } = data
  const tokenSeries = usage?.ai_tokens_daily_30d || []

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
      {/* Header card */}
      <Card>
        <div className="row" style={{ padding: '14px 16px', gap: 12, flexWrap: 'wrap' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 8,
            background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
            display: 'grid', placeItems: 'center',
            color: 'var(--accent-fg)', fontWeight: 600, fontSize: 14, flexShrink: 0,
          }}>
            {initials(company.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{company.name}</h1>
              <Pill variant={statusPillVariant(company.subscription_status)}>{company.subscription_status}</Pill>
              {company.plan && <Pill variant="ghost">{company.plan}</Pill>}
              {company.is_sandbox && <Pill variant="info">Sandbox</Pill>}
            </div>
            <div className="row mono" style={{ gap: 12, fontSize: 11, color: 'var(--text-dim)', marginTop: 4, flexWrap: 'wrap' }}>
              <span>ID #{company.id}</span>
              <span>·</span>
              <span>created {formatDateWithOptions(new Date(company.created_at), { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              {subscription?.current_period_end && (
                <>
                  <span>·</span>
                  <span>period ends {formatDateWithOptions(new Date(subscription.current_period_end), { month: 'short', day: 'numeric' })}</span>
                </>
              )}
              {subscription?.trial_ends_at && (
                <>
                  <span>·</span>
                  <span>trial ends {formatDateWithOptions(new Date(subscription.trial_ends_at), { month: 'short', day: 'numeric' })}</span>
                </>
              )}
            </div>
          </div>
          <div className="row" style={{ gap: 6, flexShrink: 0 }}>
            <button className="btn ghost" onClick={() => navigate('/tenants')}>
              <Icon name="arrow-right" size={11} style={{ transform: 'rotate(180deg)' }} />
              Back
            </button>
          </div>
        </div>
      </Card>

      {/* Soft-delete banner */}
      {company.deleted_at && (
        <Card>
          <div style={{ padding: 12, display: 'flex', gap: 10, alignItems: 'flex-start', color: 'var(--err)', fontSize: 12 }}>
            <Icon name="alert-triangle" size={14} />
            <div>
              <strong>Tenant marked for deletion.</strong>{' '}
              {company.scheduled_purge_at && (
                <>Scheduled purge: <span className="mono">{formatDateTime(company.scheduled_purge_at)}</span>. </>
              )}
              Use Restore to undo within the grace window.
            </div>
          </div>
        </Card>
      )}

      {err && (
        <Card>
          <div style={{ padding: 10, color: 'var(--err)', fontSize: 12 }}>{err}</div>
        </Card>
      )}

      {/* Active flags */}
      {flags && flags.length > 0 && (
        <Card icon="alert-triangle" title={`Active flags (${flags.length})`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {flags.map((f, i) => (
              <div key={`${f.kind}-${i}`} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                <Pill variant="warn">{f.kind}</Pill>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{f.explanation}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 12-col bento body */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 10 }}>

        {/* Lifecycle (5 cols) */}
        <div style={{ gridColumn: 'span 5' }}>
          <Card title="Lifecycle" endpoint="POST /hub/tenants/:id/{suspend,reactivate,extend-trial,hard-delete,restore}">
            <div className="row" style={{ gap: 8, marginBottom: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6 }}>
              <StatusDot tone={
                company.deleted_at ? 'err' :
                company.subscription_status === 'active' ? 'ok' :
                company.subscription_status === 'trialing' ? 'info' :
                company.subscription_status === 'past_due' ? 'err' :
                company.subscription_status === 'suspended' ? 'warn' : 'dim'
              } />
              <span style={{ fontSize: 12 }}>
                Currently <strong style={{ fontWeight: 600 }}>{company.subscription_status}</strong>
                {company.plan ? ` on ${company.plan}` : ''}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {!company.deleted_at && company.subscription_status === 'trialing' && (
                <button className="btn" style={{ justifyContent: 'flex-start' }} onClick={() => setPendingAction('extend_trial')} disabled={busy}>
                  <Icon name="clock" size={11} /> Extend trial
                </button>
              )}
              {!company.deleted_at && company.subscription_status !== 'suspended' && (
                <button className="btn" style={{ justifyContent: 'flex-start', color: 'var(--warn)' }} onClick={() => setPendingAction('suspend')} disabled={busy}>
                  <Icon name="pause" size={11} /> Suspend
                </button>
              )}
              {!company.deleted_at && company.subscription_status === 'suspended' && (
                <button className="btn" style={{ justifyContent: 'flex-start', color: 'var(--ok)' }} onClick={() => setPendingAction('reactivate')} disabled={busy}>
                  <Icon name="play" size={11} /> Reactivate
                </button>
              )}
              {company.deleted_at ? (
                <button className="btn" style={{ justifyContent: 'flex-start', color: 'var(--ok)' }} onClick={() => setPendingAction('restore')} disabled={busy}>
                  <Icon name="check" size={11} /> Restore
                </button>
              ) : (
                <button className="btn" style={{ justifyContent: 'flex-start', color: 'var(--err)' }} onClick={() => setPendingAction('hard_delete')} disabled={busy}>
                  <Icon name="x" size={11} /> Hard delete…
                </button>
              )}
            </div>

            {/* HUB-12-7 — billing admin actions. Gated on Stripe-Live; the
                handlers return 503 when STRIPE_SECRET_KEY isn't set, which
                surfaces as an inline error toast in the modal. */}
            {!company.deleted_at && (
              <>
                <div style={{ height: 1, background: 'var(--border)', margin: '12px 0 10px' }} />
                <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 6 }}>
                  Billing admin
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <button className="btn" style={{ justifyContent: 'flex-start' }} onClick={() => setPendingAction('refund')} disabled={busy}>
                    <Icon name="rotate-ccw" size={11} /> Refund…
                  </button>
                  <button className="btn" style={{ justifyContent: 'flex-start' }} onClick={() => setPendingAction('credit')} disabled={busy}>
                    <Icon name="plus" size={11} /> Add credit…
                  </button>
                  <button className="btn" style={{ justifyContent: 'flex-start' }} onClick={() => setPendingAction('one_off_invoice')} disabled={busy}>
                    <Icon name="file-text" size={11} /> One-off invoice…
                  </button>
                  <button className="btn" style={{ justifyContent: 'flex-start' }} onClick={() => setPendingAction('send_billing_email')} disabled={busy}>
                    <Icon name="mail" size={11} /> Send billing email…
                  </button>
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Activity (7 cols) */}
        <div style={{ gridColumn: 'span 7' }}>
          <Card title="AI activity · 30d" endpoint="GET /hub/tenants/:id">
            {tokenSeries.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>No AI activity in this window.</div>
            ) : (
              <AreaChart
                data={tokenSeries.map(p => p.tokens)}
                color="var(--accent)"
                height={130}
                xLabels={[
                  tokenSeries[0]?.date,
                  tokenSeries[Math.floor(tokenSeries.length / 2)]?.date,
                  tokenSeries[tokenSeries.length - 1]?.date,
                ].filter(Boolean) as string[]}
              />
            )}
          </Card>
        </div>

        {/* Usage KPIs (7 cols) */}
        <div style={{ gridColumn: 'span 7' }}>
          <Card title="Usage" endpoint="GET /hub/tenants/:id">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--border)', margin: -12, marginTop: 0 }}>
              <div style={{ background: 'var(--surface)', padding: '0' }}><KPI label="Active users" value={String(users.length)} delta={`${usage?.users_logged_in_7d ?? 0} signed in 7d`} tone="flat" /></div>
              <div style={{ background: 'var(--surface)', padding: '0' }}><KPI label="Logins 30d" value={String(usage?.users_logged_in_30d ?? 0)} delta={`${usage?.users_logged_in_7d ?? 0} in 7d`} tone="flat" /></div>
              <div style={{ background: 'var(--surface)', padding: '0' }}><KPI label="Projects 30d" value={String(usage?.projects_created_30d ?? 0)} delta="created" tone="flat" /></div>
              <div style={{ background: 'var(--surface)', padding: '0' }}><KPI label="AI tokens 30d" value={tokenSeries.reduce((a, p) => a + p.tokens, 0).toLocaleString()} delta="last 30d" tone="flat" /></div>
            </div>
          </Card>
        </div>

        {/* Feature flags (5 cols) */}
        <div style={{ gridColumn: 'span 5' }}>
          <FeatureFlagsCard companyID={company.id} plan={company.plan} />
        </div>

        {/* Subscription (6 cols) */}
        <div style={{ gridColumn: 'span 6' }}>
          <Card title="Subscription" endpoint="GET /hub/tenants/:id">
            <Field label="Stripe customer" value={subscription?.stripe_customer_id || '—'} mono />
            <Field label="Stripe subscription" value={subscription?.stripe_subscription_id || '—'} mono />
            {subscription?.stripe_credit_balance !== undefined && (
              <Field label="Credit balance" value={subscription.stripe_credit_balance > 0 ? formatCents(subscription.stripe_credit_balance) : '$0.00'} />
            )}
            <Field label="Trial ends" value={formatDateTime(subscription?.trial_ends_at)} />
            <Field label="Period end" value={formatDateTime(subscription?.current_period_end)} />
            <Field label="Created" value={formatDateTime(company.created_at)} />
          </Card>
        </div>

        {/* Billing history (6 cols) */}
        <div style={{ gridColumn: 'span 6' }}>
          <Card title="Billing history">
            {billing_history?.invoices && billing_history.invoices.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                {billing_history.invoices.slice(0, 5).map((inv: any, i: number) => (
                  <div key={i} className="row" style={{ justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                    <span className="mono">{inv.date || inv.created_at}</span>
                    <span className="mono">{inv.amount_cents ? formatCurrency(inv.amount_cents, { decimals: 2, fromCents: true }) : `$${inv.amount}`}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic', margin: 0 }}>
                {billing_history?.note || 'No billing history yet.'}
              </p>
            )}
          </Card>
        </div>

        {/* Sage usage (12 cols) */}
        <div style={{ gridColumn: 'span 12' }}>
          <SageUsageCard companyID={company.id} />
        </div>

        {/* Outbound log (12 cols) */}
        <div style={{ gridColumn: 'span 12' }}>
          <OutboundLogCard companyID={company.id} />
        </div>

        {/* Users (6 cols) */}
        <div style={{ gridColumn: 'span 6' }}>
          <Card title={`Users · ${users.length}`}>
            {users.length === 0 ? (
              <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0, fontStyle: 'italic' }}>No users yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {users.map((u, i) => (
                  <div key={u.id} className="row" style={{
                    justifyContent: 'space-between', padding: '8px 0',
                    borderTop: i ? '1px solid var(--border)' : 0,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>{u.name}</div>
                      <div className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>{u.email}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Pill variant="ghost">{u.role}</Pill>
                      <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                        {timeAgo(u.last_login_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Audit log (6 cols) */}
        <div style={{ gridColumn: 'span 6' }}>
          <Card title={`Audit timeline · ${audit.length}`} endpoint="GET /hub/tenants/:id/audit-log">
            {audit.length === 0 ? (
              <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0, fontStyle: 'italic' }}>No audit events yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {audit.slice(0, 50).map(row => (
                  <div key={row.id} className="row" style={{ gap: 12, fontSize: 11.5 }}>
                    <span className="mono" style={{ width: 70, color: 'var(--text-dim)', flexShrink: 0 }}>{timeAgo(row.created_at)}</span>
                    <StatusDot tone={row.severity === 'critical' ? 'err' : row.severity === 'warn' ? 'warn' : 'info'} />
                    <span className="mono" style={{ width: 160, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.initiated_by || '—'}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>{row.action}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Confirm modals */}
      <ConfirmModal
        open={pendingAction === 'suspend'}
        title="Suspend tenant?"
        body="Users will be locked out immediately. You can reactivate later from this page."
        confirmLabel="Suspend"
        confirmTone="warn"
        busy={busy}
        onCancel={() => setPendingAction(null)}
        onConfirm={runAction}
      />
      <ConfirmModal
        open={pendingAction === 'reactivate'}
        title="Reactivate tenant?"
        body="Restores user access. Subscription status flips back to its prior state."
        confirmLabel="Reactivate"
        confirmTone="ok"
        busy={busy}
        onCancel={() => setPendingAction(null)}
        onConfirm={runAction}
      />
      <ConfirmModal
        open={pendingAction === 'hard_delete'}
        title="Hard delete tenant?"
        confirmLabel="Delete"
        confirmTone="err"
        busy={busy || deleteConfirm.trim().toLowerCase() !== company.name.trim().toLowerCase()}
        onCancel={() => { setPendingAction(null); setDeleteConfirm('') }}
        onConfirm={runAction}
      >
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
          Marks the tenant deleted and revokes all active sessions immediately.
          A 24-hour grace window leaves the data restorable. After the grace
          window the tenant is purged.
        </div>
        <label style={{ display: 'block' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Type "{company.name}" to confirm:
          </span>
          <input
            type="text"
            value={deleteConfirm}
            onChange={e => setDeleteConfirm(e.target.value)}
            placeholder={company.name}
            style={{
              marginTop: 6, width: '100%', padding: '6px 10px', borderRadius: 5,
              background: 'var(--surface-2)', color: 'var(--text)',
              border: '1px solid var(--border-strong)', fontFamily: 'inherit', fontSize: 12,
              outline: 'none',
            }}
          />
        </label>
      </ConfirmModal>
      <ConfirmModal
        open={pendingAction === 'restore'}
        title="Restore tenant?"
        body="Clears the soft-delete and lifts the JWT revocation. Subscription stays suspended; reactivate separately if billing should resume."
        confirmLabel="Restore"
        confirmTone="ok"
        busy={busy}
        onCancel={() => setPendingAction(null)}
        onConfirm={runAction}
      />
      <ConfirmModal
        open={pendingAction === 'refund'}
        title="Issue refund"
        confirmLabel="Refund"
        confirmTone="warn"
        busy={busy || !refundInvoiceID.trim() || !refundReason.trim()}
        onCancel={() => { setPendingAction(null); resetBillingForms() }}
        onConfirm={runAction}
      >
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
          Refunds the customer via Stripe. Cards typically credit back within 5–10 business days. Audit-logged.
        </div>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Stripe invoice ID</span>
          <input
            type="text"
            value={refundInvoiceID}
            onChange={e => setRefundInvoiceID(e.target.value)}
            placeholder="in_1AbC2dEfG3..."
            style={billingInputStyle}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Amount (leave blank for full invoice refund)</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={refundAmount}
            onChange={e => setRefundAmount(e.target.value)}
            placeholder="e.g. 49.00"
            style={billingInputStyle}
          />
        </label>
        <label style={{ display: 'block' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Reason (audit log)</span>
          <input
            type="text"
            value={refundReason}
            onChange={e => setRefundReason(e.target.value)}
            placeholder="why this refund"
            style={billingInputStyle}
          />
        </label>
      </ConfirmModal>
      <ConfirmModal
        open={pendingAction === 'credit'}
        title="Add account credit"
        confirmLabel="Add credit"
        confirmTone="info"
        busy={busy || !creditAmount.trim() || !creditReason.trim()}
        onCancel={() => { setPendingAction(null); resetBillingForms() }}
        onConfirm={runAction}
      >
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
          Adds a credit to the customer's Stripe balance — auto-applies against the next invoice. Enter a positive number; system inverts the sign.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 10 }}>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Amount</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={creditAmount}
              onChange={e => setCreditAmount(e.target.value)}
              placeholder="e.g. 25.00"
              style={billingInputStyle}
            />
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Currency</span>
            <select
              value={creditCurrency}
              onChange={e => setCreditCurrency(e.target.value)}
              style={billingInputStyle}
            >
              <option value="cad">CAD</option>
              <option value="usd">USD</option>
            </select>
          </label>
        </div>
        <label style={{ display: 'block' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Reason (audit log)</span>
          <input
            type="text"
            value={creditReason}
            onChange={e => setCreditReason(e.target.value)}
            placeholder="why this credit"
            style={billingInputStyle}
          />
        </label>
      </ConfirmModal>
      <ConfirmModal
        open={pendingAction === 'one_off_invoice'}
        title="Send one-off invoice"
        confirmLabel={oneOffSendNow ? 'Send invoice' : 'Save as draft'}
        confirmTone="info"
        busy={busy || !oneOffAmount.trim() || !oneOffDescription.trim() || !oneOffReason.trim()}
        onCancel={() => { setPendingAction(null); resetBillingForms() }}
        onConfirm={runAction}
      >
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
          Bills the customer for a one-time charge outside the regular subscription cycle. When "Send now" is on, Stripe emails the invoice immediately.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 10 }}>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Amount</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={oneOffAmount}
              onChange={e => setOneOffAmount(e.target.value)}
              placeholder="e.g. 250.00"
              style={billingInputStyle}
            />
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Currency</span>
            <select
              value={oneOffCurrency}
              onChange={e => setOneOffCurrency(e.target.value)}
              style={billingInputStyle}
            >
              <option value="cad">CAD</option>
              <option value="usd">USD</option>
            </select>
          </label>
        </div>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Description (shown to customer)</span>
          <input
            type="text"
            value={oneOffDescription}
            onChange={e => setOneOffDescription(e.target.value)}
            placeholder="e.g. Custom integration setup"
            style={billingInputStyle}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Reason (audit log)</span>
          <input
            type="text"
            value={oneOffReason}
            onChange={e => setOneOffReason(e.target.value)}
            placeholder="internal reason for the audit log"
            style={billingInputStyle}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={oneOffSendNow} onChange={e => setOneOffSendNow(e.target.checked)} />
          Send invoice email to customer now
        </label>
      </ConfirmModal>
      <ConfirmModal
        open={pendingAction === 'send_billing_email'}
        title="Send billing email"
        confirmLabel="Send"
        confirmTone="info"
        busy={busy}
        onCancel={() => { setPendingAction(null); resetBillingForms() }}
        onConfirm={runAction}
      >
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
          Sends an email to all admin users on this tenant. Logged to the outbound delivery log.
        </div>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Template</span>
          <select
            value={billingEmailTemplate}
            onChange={e => setBillingEmailTemplate(e.target.value as typeof billingEmailTemplate)}
            style={billingInputStyle}
          >
            <option value="trial-ending">Trial ending soon</option>
            <option value="past-due">Payment past due</option>
            <option value="dunning">Final dunning notice</option>
            <option value="custom">Custom</option>
          </select>
        </label>
        {billingEmailTemplate === 'custom' && (
          <>
            <label style={{ display: 'block', marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Subject</span>
              <input
                type="text"
                value={billingEmailSubject}
                onChange={e => setBillingEmailSubject(e.target.value)}
                placeholder="Email subject"
                style={billingInputStyle}
              />
            </label>
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Body (HTML)</span>
              <textarea
                value={billingEmailBody}
                onChange={e => setBillingEmailBody(e.target.value)}
                placeholder="<p>Hi, ...</p>"
                rows={6}
                style={{ ...billingInputStyle, resize: 'vertical' }}
              />
            </label>
          </>
        )}
      </ConfirmModal>
      <ConfirmModal
        open={pendingAction === 'extend_trial'}
        title="Extend trial"
        confirmLabel="Extend"
        confirmTone="info"
        busy={busy}
        onCancel={() => setPendingAction(null)}
        onConfirm={runAction}
      >
        <label style={{ display: 'block' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Extend trial by how many days?</span>
          <input
            type="number"
            min={1}
            value={extendDays}
            onChange={e => setExtendDays(e.target.value)}
            style={{
              marginTop: 6, width: 100, padding: '6px 10px', borderRadius: 5,
              background: 'var(--surface-2)', color: 'var(--text)',
              border: '1px solid var(--border-strong)', fontFamily: 'inherit', fontSize: 12,
              outline: 'none',
            }}
          />
        </label>
      </ConfirmModal>
    </div>
  )
}

// HUB-12-7 — shared input style for the billing-action modals.
const billingInputStyle: React.CSSProperties = {
  marginTop: 6,
  width: '100%',
  padding: '6px 10px',
  borderRadius: 5,
  background: 'var(--surface-2)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  fontFamily: 'inherit',
  fontSize: 12,
  outline: 'none',
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase',
        letterSpacing: '0.08em', fontWeight: 600,
      }}>{label}</div>
      <div className={mono ? 'mono' : ''} style={{
        fontSize: 12.5, color: 'var(--text)', marginTop: 2, wordBreak: 'break-all',
      }}>
        {value}
      </div>
    </div>
  )
}

function FeatureFlagsCard({ companyID, plan }: { companyID: number; plan: string | null }) {
  const [data, setData] = useState<HubTenantFeatureFlags | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [bulkBusy, setBulkBusy] = useState<string | null>(null)
  const [bulkScope, setBulkScope] = useState<'plan' | 'all'>(plan ? 'plan' : 'all')

  useEffect(() => {
    hubTenants.getFeatureFlags(companyID).then(setData).catch(e => {
      setErr(e instanceof Error ? e.message : 'Failed to load feature flags')
    })
  }, [companyID])

  const toggle = async (key: string, current: any) => {
    setBusy(true)
    setErr(null)
    try {
      const next = !current
      const updated = await hubTenants.updateFeatureFlags(companyID, { [key]: next })
      setData(updated)
      posthogCapture('hub_feature_flag_toggled', { tenant_id: companyID, flag: key, enabled: next })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Toggle failed')
    } finally {
      setBusy(false)
    }
  }

  const bulk = async (key: string, value: boolean) => {
    const scope = bulkScope === 'plan' && plan ? plan : 'all'
    if (!confirm(`Set "${key}" = ${value} on ALL tenants ${scope === 'all' ? '(everyone)' : 'on plan ' + scope}?`)) return
    setBulkBusy(key)
    setErr(null)
    try {
      const res = await hubFeatureFlags.bulkToggle(key, value, scope)
      posthogCapture('hub_feature_flag_bulk_toggled', { tenant_id: companyID, flag: key, enabled: value })
      alert(`Updated ${res.rows} tenant(s).`)
      const refreshed = await hubTenants.getFeatureFlags(companyID)
      setData(refreshed)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Bulk toggle failed')
    } finally {
      setBulkBusy(null)
    }
  }

  if (!data && !err) {
    return <Card title="Feature flags"><div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Loading…</div></Card>
  }

  const flags = data?.flags || {}
  const catalog = data?.catalog || []
  const customKeys = Object.keys(flags).filter(k => !catalog.some(c => c.key === k))

  return (
    <Card
      title="Feature flags"
      endpoint="PATCH /hub/tenants/:id/feature-flags"
      right={
        <div className="row" style={{ gap: 8, fontSize: 10.5, color: 'var(--text-dim)' }}>
          <label style={{ cursor: plan ? 'pointer' : 'not-allowed' }}>
            <input type="radio" checked={bulkScope === 'plan'} disabled={!plan}
              onChange={() => setBulkScope('plan')} style={{ marginRight: 4 }} />
            plan
          </label>
          <label style={{ cursor: 'pointer' }}>
            <input type="radio" checked={bulkScope === 'all'}
              onChange={() => setBulkScope('all')} style={{ marginRight: 4 }} />
            all
          </label>
        </div>
      }
    >
      {err && <div className="alert-chip err" style={{ marginBottom: 8 }}>{err}</div>}
      {(catalog.length === 0 && customKeys.length === 0) ? (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>
          No flags defined.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {catalog.map((c, i) => {
            const enabled = flags[c.key] === true
            return (
              <div key={c.key} className="row" style={{
                padding: '8px 0', borderTop: i ? '1px solid var(--border)' : 0, gap: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="mono" style={{ fontSize: 11.5, color: 'var(--text)' }}>{c.key}</span>
                    <Pill variant={enabled ? 'ok' : 'ghost'}>{enabled ? 'on' : 'off'}</Pill>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{c.label}</div>
                </div>
                <button className="btn" onClick={() => toggle(c.key, flags[c.key])} disabled={busy} style={{ fontSize: 10.5, height: 22 }}>
                  {enabled ? 'Disable' : 'Enable'}
                </button>
                <button className="btn ghost" onClick={() => bulk(c.key, !enabled)} disabled={bulkBusy === c.key} style={{ fontSize: 10.5, height: 22 }}
                  title={`Apply to ${bulkScope === 'plan' && plan ? plan : 'all plans'}`}>
                  {bulkBusy === c.key ? '…' : 'Bulk'}
                </button>
              </div>
            )
          })}
          {customKeys.length > 0 && (
            <div style={{ marginTop: 10, padding: 8, borderRadius: 6, background: 'var(--surface-2)' }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, fontWeight: 600 }}>
                Custom (not in catalog)
              </div>
              <pre className="mono" style={{ margin: 0, fontSize: 10.5, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(Object.fromEntries(customKeys.map(k => [k, flags[k]])), null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

function SageUsageCard({ companyID }: { companyID: number }) {
  const [data, setData] = useState<HubTenantSageUsage | null>(null)
  const [windowDays, setWindowDays] = useState<30 | 90>(30)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    hubTenants.sageUsage(companyID, windowDays)
      .then(d => { if (!cancelled) { setData(d); setErr(null); setLoading(false) } })
      .catch(e => { if (!cancelled) { setErr(e instanceof Error ? e.message : 'Failed to load Sage usage'); setLoading(false) } })
    return () => { cancelled = true }
  }, [companyID, windowDays])

  const tabRight = (
    <div className="row" style={{ gap: 4, padding: 2, borderRadius: 5, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
      {([30, 90] as const).map(d => (
        <button
          key={d}
          onClick={() => setWindowDays(d)}
          className="btn"
          style={{
            height: 20, padding: '0 8px', fontSize: 10.5,
            background: windowDays === d ? 'var(--surface)' : 'transparent',
            color: windowDays === d ? 'var(--text)' : 'var(--text-muted)',
            border: 'none',
          }}
        >
          {d}d
        </button>
      ))}
    </div>
  )

  if (loading && !data) {
    return <Card title="Sage cost / usage" endpoint="GET /hub/tenants/:id/sage-usage" right={tabRight}>
      <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Loading…</div>
    </Card>
  }
  if (err) {
    return <Card title="Sage cost / usage" endpoint="GET /hub/tenants/:id/sage-usage" right={tabRight}>
      <div style={{ fontSize: 12, color: 'var(--err)' }}>{err}</div>
    </Card>
  }
  if (!data) return null

  const ceilingCents = data.monthly_budget_cents ?? 0
  const usagePct = ceilingCents > 0 ? (data.mtd_cost_cents / ceilingCents) * 100 : 0
  const projectedPct = ceilingCents > 0 ? (data.projected_cost_cents / ceilingCents) * 100 : 0
  const sortedThresholds = [...(data.alert_thresholds || [])].sort((a, b) => a - b)
  const ceilingTone =
    !ceilingCents ? 'var(--text-dim)' :
    usagePct >= 100 ? 'var(--err)' :
    usagePct >= (sortedThresholds[sortedThresholds.length - 1] ?? 80) ? 'var(--warn)' :
    'var(--ok)'

  const trendMaxCents = Math.max(...data.trend.map(p => p.cost_cents), 1)

  return (
    <Card title="Sage cost / usage" endpoint="GET /hub/tenants/:id/sage-usage" right={tabRight}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--border)', margin: '0 0 12px' }}>
        <div style={{ background: 'var(--surface)' }}><KPI label="MTD spend" value={formatCents(data.mtd_cost_cents)} /></div>
        <div style={{ background: 'var(--surface)' }}><KPI label="MTD tokens" value={data.mtd_tokens.toLocaleString()} /></div>
        <div style={{ background: 'var(--surface)' }}><KPI label="MTD calls" value={data.mtd_calls.toLocaleString()} /></div>
        <div style={{ background: 'var(--surface)' }}><KPI label="Projected (mo)" value={formatCents(data.projected_cost_cents)} /></div>
      </div>

      {ceilingCents > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
              Cost ceiling
            </span>
            <span className="mono" style={{ fontSize: 11.5, color: ceilingTone, fontWeight: 600 }}>
              {formatCents(data.mtd_cost_cents)} / {formatCents(ceilingCents)} ({usagePct.toFixed(0)}%)
            </span>
          </div>
          <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'var(--surface-2)', overflow: 'hidden', border: '1px solid var(--border)' }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, height: '100%',
              width: `${Math.min(100, usagePct)}%`, background: ceilingTone,
            }} />
            {projectedPct > usagePct && projectedPct <= 100 && (
              <div style={{
                position: 'absolute', top: 0, height: '100%',
                left: `${Math.min(100, usagePct)}%`,
                width: `${Math.min(100 - usagePct, projectedPct - usagePct)}%`,
                background: ceilingTone, opacity: 0.3,
              }} />
            )}
            {sortedThresholds.map(t => (
              <div key={t} style={{
                position: 'absolute', top: 0, height: '100%',
                left: `${Math.min(100, t)}%`, width: 1,
                background: t >= 100 ? 'var(--err)' : 'var(--warn)',
              }} />
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          Daily spend ({windowDays}d)
        </div>
        {data.trend.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>No Sage activity in this window.</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 56 }}>
            {data.trend.map((p, i) => (
              <div
                key={p.date + i}
                title={`${p.date}: ${formatCents(p.cost_cents)} · ${p.tokens.toLocaleString()} tokens`}
                style={{
                  flex: 1, background: 'var(--accent)', opacity: 0.7, borderRadius: 1,
                  height: `${Math.max(2, (p.cost_cents / trendMaxCents) * 100)}%`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            Top users (30d)
          </div>
          {data.top_users.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>No user activity yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {data.top_users.map((u, i) => (
                <div key={u.user_id} className="row" style={{
                  justifyContent: 'space-between', padding: '5px 0',
                  borderTop: i ? '1px solid var(--border)' : 0, fontSize: 11.5, color: 'var(--text-muted)',
                }}>
                  <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                    {u.name || u.email}
                  </span>
                  <span className="mono" style={{ flexShrink: 0, fontSize: 11 }}>
                    {u.tokens.toLocaleString()} · {formatCents(u.cost_cents)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            Features (30d)
          </div>
          {data.feature_breakdown.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>No feature activity yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {data.feature_breakdown.map((f, i) => (
                <div key={f.feature} className="row" style={{
                  justifyContent: 'space-between', padding: '5px 0',
                  borderTop: i ? '1px solid var(--border)' : 0, fontSize: 11.5, color: 'var(--text-muted)',
                }}>
                  <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                    {f.feature}
                  </span>
                  <span className="mono" style={{ flexShrink: 0, fontSize: 11 }}>
                    {f.calls} · {formatCents(f.cost_cents)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

function OutboundLogCard({ companyID }: { companyID: number }) {
  const [data, setData] = useState<HubTenantOutboundLog | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'' | 'sent' | 'failed' | 'skipped'>('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    hubTenants.outboundLog(companyID, { limit: 100, status: statusFilter || undefined })
      .then(d => { if (!cancelled) { setData(d); setErr(null); setLoading(false) } })
      .catch(e => { if (!cancelled) { setErr(e instanceof Error ? e.message : 'Failed to load outbound log'); setLoading(false) } })
    return () => { cancelled = true }
  }, [companyID, statusFilter])

  const items = data?.items || []
  const summary = data?.summary_7d || []

  const totals: Record<string, number> = {}
  for (const s of summary) {
    totals[s.status] = (totals[s.status] || 0) + s.count
  }

  const statusVariant = (s: string): 'ok' | 'err' | 'warn' | 'ghost' => {
    if (s === 'sent') return 'ok'
    if (s === 'failed') return 'err'
    if (s === 'skipped') return 'warn'
    return 'ghost'
  }

  const tabRight = (
    <div className="row" style={{ gap: 4, padding: 2, borderRadius: 5, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
      {(['', 'sent', 'failed', 'skipped'] as const).map(s => (
        <button
          key={s || 'all'}
          onClick={() => setStatusFilter(s)}
          className="btn"
          style={{
            height: 20, padding: '0 8px', fontSize: 10.5,
            background: statusFilter === s ? 'var(--surface)' : 'transparent',
            color: statusFilter === s ? 'var(--text)' : 'var(--text-muted)',
            border: 'none', textTransform: s ? 'capitalize' : 'none',
          }}
        >
          {s || 'All'}
        </button>
      ))}
    </div>
  )

  return (
    <Card title="Outbound delivery log" endpoint="GET /hub/tenants/:id/outbound-log" right={tabRight}>
      {loading && !data && <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Loading…</div>}
      {err && <div style={{ fontSize: 12, color: 'var(--err)' }}>{err}</div>}
      {!loading && !err && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: 'var(--border)', margin: '0 0 12px' }}>
            <div style={{ background: 'var(--surface)' }}><KPI label="7d sent" value={totals['sent'] || 0} tone="up" /></div>
            <div style={{ background: 'var(--surface)' }}><KPI label="7d failed" value={totals['failed'] || 0} tone={(totals['failed'] || 0) > 0 ? 'down' : 'flat'} /></div>
            <div style={{ background: 'var(--surface)' }}><KPI label="7d skipped" value={totals['skipped'] || 0} /></div>
            <div style={{ background: 'var(--surface)' }}><KPI label="7d total" value={Object.values(totals).reduce((a, v) => a + v, 0)} /></div>
          </div>

          {items.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', fontStyle: 'italic' }}>
              No outbound deliveries recorded{statusFilter ? ` (filtered to "${statusFilter}")` : ''}.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
              {items.map(row => (
                <div key={row.id} style={{
                  padding: 8, borderRadius: 6, background: 'var(--surface-2)',
                  borderLeft: `3px solid var(--${row.status === 'sent' ? 'ok' : row.status === 'failed' ? 'err' : 'warn'})`,
                }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div className="row" style={{ gap: 6, minWidth: 0, flex: 1 }}>
                      <Pill variant={statusVariant(row.status)}>{row.status}</Pill>
                      <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{row.channel}</span>
                      {row.event_kind && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.event_kind}</span>}
                    </div>
                    <span style={{ fontSize: 10.5, color: 'var(--text-dim)', flexShrink: 0 }}>{timeAgo(row.created_at)}</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--text)', wordBreak: 'break-all' }}>
                    {row.recipient}
                    {row.subject && <span style={{ color: 'var(--text-muted)' }}> — {row.subject}</span>}
                  </div>
                  {row.status_code > 0 && (
                    <div className="mono" style={{ marginTop: 3, fontSize: 10.5, color: 'var(--text-dim)' }}>
                      HTTP {row.status_code}
                    </div>
                  )}
                  {row.error_message && (
                    <div className="mono" style={{
                      marginTop: 5, padding: '5px 7px', borderRadius: 4,
                      background: 'var(--surface)', color: 'var(--err)', fontSize: 10.5,
                      wordBreak: 'break-all',
                    }}>
                      {row.error_message.length > 240 ? row.error_message.slice(0, 240) + '…' : row.error_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

function ConfirmModal({
  open, title, body, children, confirmLabel, confirmTone, busy, onCancel, onConfirm,
}: {
  open: boolean
  title: string
  body?: string
  children?: React.ReactNode
  confirmLabel: string
  confirmTone: 'info' | 'err' | 'ok' | 'warn'
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  if (!open) return null
  const toneColor =
    confirmTone === 'info' ? 'var(--info)' :
    confirmTone === 'err' ? 'var(--err)' :
    confirmTone === 'ok' ? 'var(--ok)' :
    'var(--warn)'
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: 420 }}>
        <div className="head">
          <span className="title">{title}</span>
        </div>
        <div className="body">
          {body && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>{body}</p>}
          {children && <div style={{ marginBottom: 12 }}>{children}</div>}
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn ghost" onClick={onCancel}>Cancel</button>
            <button className="btn" onClick={onConfirm} disabled={busy} style={{ color: toneColor, borderColor: toneColor, opacity: busy ? 0.5 : 1 }}>
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
